import useDevice from "@hooks/useDevice";
import NeuronProvider from "@web2/providers/NeuronProvider";
import type { ReactElement, ReactNode } from "react";
import { StrictMode, useEffect } from "react";
import type { LinksFunction } from "react-router";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";

import { useArticleSEO } from "./components/Article/ArticleSEO";
import CommonRender from "./components/CommonRender/CommonRender";
import { useCustomParams } from "./hooks/useCustomParams";
import useGAData from "./hooks/useGAData";
import { useIsArticlePage } from "./hooks/useIsArticlePage";
import { fetchEnvelope, useLiveRampV2 } from "./hooks/useLiverampV2";
import { useRouteResponse } from "./hooks/useRouteResponse";
// links
import styles from "./styles/index.css?url";
export const links: LinksFunction = () => [{ rel: "stylesheet", href: styles }];

// GTM Tag loaded in CSR to avoid hydration error
function GoogleTagManager({ id }: { id: string }) {
  useEffect(() => {
    (function (w, d, s, i) {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s);
      (j as HTMLScriptElement).async = true;
      (j as HTMLScriptElement).src =
        "https://www.googletagmanager.com/gtm.js?id=" + i;
      f.parentNode?.insertBefore(j, f);
    })(window, document, "script", id);
  }, [id]);

  // SSR & CSR 初始渲染都返回 null，不参与 DOM 结构
  return null;
}

// NewRelic config

const NR_ACCOUNT_ID = import.meta.env.VITE_NEW_RELIC_ID || "";
const NR_LICENSE_KEY = import.meta.env.VITE_NEW_RELIC_KEY || "";
const NR_CONFIGURED = !!(NR_ACCOUNT_ID && NR_LICENSE_KEY);

const NR_SNIPPET = !NR_CONFIGURED
  ? ""
  : `
;window.NREUM||(NREUM={});NREUM.init={distributed_tracing:{enabled:true},privacy:{cookies_enabled:true},ajax:{deny_list:["bam.nr-data.net"]}};
;NREUM.loader_config={accountID:"225023",trustKey:"225023",agentID:"${NR_ACCOUNT_ID}",licenseKey:"${NR_LICENSE_KEY}",applicationID:"${NR_ACCOUNT_ID}"};
;NREUM.info={beacon:"bam.nr-data.net",errorBeacon:"bam.nr-data.net",licenseKey:"${NR_LICENSE_KEY}",applicationID:"${NR_ACCOUNT_ID}",sa:1};
`.trim();

console.log("NR from env:", NR_ACCOUNT_ID, NR_LICENSE_KEY, NR_CONFIGURED);

export default function App(): ReactElement {
  useDevice();
  useLiveRampV2({ fetchEnvelope });

  return (
    <StrictMode>
      <NeuronProvider>
        <ScrollRestoration />
        <CommonRender>
          <Outlet />
        </CommonRender>
        <span
          id="package-version-tag"
          data-testid="package-version-tag"
          data-version={__APP_VERSION__}
          style={{ display: "none" }}
        >
          {__APP_VERSION__}
        </span>
      </NeuronProvider>
    </StrictMode>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const {
    gaLevel2,
    chapter1,
    contenttype,
    keyword,
    author,
    contentcat,
    articleid,
    pubdate,
    cd_version,
    visitorcat,
    pagination,
  } = useGAData();

  // NewRelic config
  const id = import.meta.env.VITE_NEW_RELIC_ID || "";
  const key = import.meta.env.VITE_NEW_RELIC_KEY || "";
  const isConfigured = !!(id && key);
  if (!isConfigured) {
    console.warn("Skipping NewRelic import.meta.env", {
      VITE_NEW_RELIC_ID: id,
      VITE_NEW_RELIC_KEY: key,
    });
  }

  const { level1 = "" } = useCustomParams();
  const { articleSEOStr = "" } = useArticleSEO();
  const isArticlePage = useIsArticlePage();
  const { article, iasInfo } = useRouteResponse("articlePage");
  const gs_channels = iasInfo?.gsChannels || "";
  const gsKeywords = gs_channels.split(",").filter(Boolean);
  const fr = iasInfo?.fr || "";

  const targetings = [
    { key: "fr", value: fr },
    { key: "gs_channels", value: gs_channels },
    {
      key: "page",
      value: isArticlePage
        ? "article"
        : level1 == "keyword"
          ? "tag"
          : level1 == ""
            ? "home"
            : "listing",
    },
  ].concat(
    isArticlePage
      ? [
          {
            key: "rtags",
            value: article?.tags.map((i) => i.name).join(",") || "",
          },
          { key: "rarticleid", value: article?.id || "" },
        ]
      : [],
  );

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <html lang="zh-Hans" className="theme-light max-lg:no-scrollbar">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          rel="preload"
          as="style"
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400..700&display=swap"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400..700&display=swap"
          rel="stylesheet"
        />

        {/* Article Page SEO */}
        {isArticlePage ? (
          <script dangerouslySetInnerHTML={{ __html: articleSEOStr }} />
        ) : null}
        {/* End Article Page SEO */}

        {/* NewRelic */}
        <script dangerouslySetInnerHTML={{ __html: NR_SNIPPET }} />
        <script
          type="text/javascript"
          src="/scripts/nr-loader-spa.1.286.0.min.js"
          async
        />
        {/* End NewRelic */}

        {/* GA Data */}
        <script
          id="ga_data_layer"
          dangerouslySetInnerHTML={{
            __html: `
            var _data = {
              "level2":         "${gaLevel2}", 
              "chapter1":       "${chapter1}",
              "contenttype":    "${contenttype}",
              "keyword":        "${keyword}",
              "author":         "${author}",
              "contentcat":     "${contentcat}",
              "articleid":      "${articleid}",
              "pubdate":        "${pubdate}",
              "cue_articleid":  "${articleid}",
              "cd_version":     "${cd_version}",
              "visitorcat":     "${visitorcat}",
              "pagination":     "${pagination}",
            };
          `,
          }}
        />
        {/* End GA Data */}

        {/* Permutive */}
        {/* @see https://sph.atlassian.net/wiki/spaces/LOT/pages/1026687058/Permutive */}
        {/* <!-- suid, aim to check suid field info  --> */}
        <script
          src="https://cdn.sphlabs.com/suid/suid.min.js"
          type="text/javascript"
        />
        <script
          id="permutive-script-init"
          dangerouslySetInnerHTML={{
            __html: `
              !function(e,o,n,i){if(!e){e=e||{},window.permutive=e,e.q=[];var t=function(){return([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,function(e){return(e^(window.crypto||window.msCrypto).getRandomValues(new Uint8Array(1))[0]&15>>e/4).toString(16)})};e.config=i||{},e.config.apiKey=o,e.config.workspaceId=n,e.config.environment=e.config.environment||"production",(window.crypto||window.msCrypto)&&(e.config.viewId=t());for(var g=["addon","identify","track","trigger","query","segment","segments","ready","on","once","user","consent"],r=0;r<g.length;r++){var w=g[r];e[w]=function(o){return function(){var n=Array.prototype.slice.call(arguments,0);e.q.push({functionName:o,arguments:n})}}(w)}}}(window.permutive,"ab403253-b305-47fa-a31b-3efb2473166f","5f876161-9740-4cc8-9b64-4585990b2690",{"consentRequired": false});
            window.googletag=window.googletag||{},window.googletag.cmd=window.googletag.cmd||[],window.googletag.cmd.push(function(){if(0===window.googletag.pubads().getTargeting("permutive").length){var e=window.localStorage.getItem("_pdfps");window.googletag.pubads().setTargeting("permutive",e?JSON.parse(e):[]);var o=window.localStorage.getItem("permutive-id");o&&(window.googletag.pubads().setTargeting("puid",o),window.googletag.pubads().setTargeting("ptime",Date.now().toString())),window.permutive.config.viewId&&window.googletag.pubads().setTargeting("prmtvvid",window.permutive.config.viewId),window.permutive.config.workspaceId&&window.googletag.pubads().setTargeting("prmtvwid",window.permutive.config.workspaceId)}});

            function getCookie(name) {
              const cookies = document.cookie.split("; ");
              for (let cookie of cookies) {
                const [key, value] = cookie.split("=");
                if (key === name) {
                  return decodeURIComponent(value);
                }
              }
              return null;
            }

            const per_userid = getCookie("mysphw") || "";
            const ptid = getCookie("ptid") || "";
            const per_suid = getCookie("suid") || "";
            const sph_seg = (getCookie("sph_seg") || "").toLowerCase().split(",").filter(Boolean);

            permutive.identify([
              {
                "id": per_userid,
                "tag": "userid",
                "priority": 0
              },
              {
                "id": ptid, //SHA256 hashed email (email in lower case without leading and trailing spaces)
                "tag": "ptid",
                "priority": 1
              },
              {
                "id": per_suid,
                "tag": "suid",
                "priority": 2
              }
            ])

            const data = window._data || {};
            const keywords = (data.keyword || "").toLowerCase().split(",").filter(Boolean);

            permutive.addon('web',{
              "page": {
                "contenttype": data.contenttype,
                "sph_iab_list": [],
                "videoTags": [],
                "keywords": keywords,
                "sph_seg": sph_seg,
                "videoTitle": "",
                "videoDuration": 0,
                "title": "",
                "pagination": 1,
                "videoID": "",
                "articleid": data.articleid,
                "chapter1": "${level1}",
                "chapter2": "",
                "chapter3": "",
                "visitorcat": data.visitorcat,
                "story_threads": "",
                "videoSource": "",
                "gsKeywords": ${JSON.stringify(gsKeywords)},
                "contentcat": data.contenttype,
                "videoAccountID": "",
                "videoAutoPlayed": true,
                "sph_bs_list": [],
                "videoCategory": ""
              }
            });
          `,
          }}
        />

        <script
          id="permutive-script"
          async
          src="https://5f876161-9740-4cc8-9b64-4585990b2690.edge.permutive.app/5f876161-9740-4cc8-9b64-4585990b2690-web.js"
        />

        {/* End Permutive */}

        {/* Lotame */}
        <link rel="preconnect" href="https://tags.crwdcntrl.net" />
        <link rel="preconnect" href="https://bcp.crwdcntrl.net" />
        <link rel="dns-prefetch" href="https://tags.crwdcntrl.net" />
        <link rel="dns-prefetch" href="https://bcp.crwdcntrl.net" />
        <script
          dangerouslySetInnerHTML={{
            __html: `
            // LOTAME_SCRIPT
            !(function () {
              // Lotame config
              var lotameClientId = "12374";
              var lotameTagInput = {
                data: {},
                config: {
                  clientId: Number(lotameClientId),
                },
              };

              // Lotame initialization
              var lotameConfig = lotameTagInput.config || {};
              var namespace = (window["lotame_" + lotameConfig.clientId] = {});
              namespace.config = lotameConfig;
              namespace.data = lotameTagInput.data || {};
              namespace.cmd = namespace.cmd || [];
            })();
          `,
          }}
        />
        <script async src="https://tags.crwdcntrl.net/lt/c/12374/lt.min.js" />
        {/* End Lotame */}

        {/* Google Tag Manager */}
        <GoogleTagManager id="GTM-WZ8CG68" />

        {/* End Google Tag Manager */}
        {/* Google Ads */}
        <script
          id="ads_checker_script"
          type="text/javascript"
          src="https://adtag.sphdigital.com/tag/ads/ads_checker.js"
        />
        <script
          id="ads_control_script"
          type="text/javascript"
          src={import.meta.env.VITE_AD_CONTROL_URL || ""}
        />
        <script
          id="google_ads_script"
          async
          type="text/javascript"
          src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
          crossOrigin="anonymous"
        />
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
            var googletag = googletag || {};
            googletag.cmd = googletag.cmd || [];
            googletag.cmd.push(function () {
              googletag.pubads().disableInitialLoad(); // Disable initial load
              googletag.pubads().enableSingleRequest();
              googletag.pubads().collapseEmptyDivs();
              ${targetings
                .map(
                  (t) =>
                    `googletag.pubads().setTargeting(${JSON.stringify(t.key)}, ${JSON.stringify(t.value)});`,
                )
                .join("\n")}
              googletag.enableServices();
            });
          `,
          }}
        />
        <script
          type="text/javascript"
          src="/scripts/prestitial-postitial-ad-v1.js"
        />
        {/* End Google Ads */}
        <Meta />
        <Links />
      </head>

      <body>
        {/* Prestitial Ad */}
        <div id="prestitial_ad_overlay" />
        <div id="dfp-ad-prestitial" />
        {/* Postitial Ad */}
        <div id="dfp-ad-postitial" />

        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            title="Google Tag Manager"
            src="https://www.googletagmanager.com/ns.html?id=GTM-WZ8CG68"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}

        <div id="app">{children}</div>

        <Scripts />
      </body>
    </html>
  );
}
